import dynamic from 'next/dynamic';
import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

import { clearSearchFiltersForPdp } from 'store/slice/searchSlice';
import {
  IConsumerSearchFactsWindowData,
  IRelatedProductData,
} from 'productTemplate/types/FactsWindowData';
import { getWants } from 'productTemplate/services/wants';
import { IFeaturedInCollection } from 'productTemplate/types/FeaturedInCollection';
import { IGmcOffers } from 'shared/types/Offer';
import { IProductTemplate } from 'shared/types/ProductTemplate';
import { LOCALES } from 'localization/constants/configEnums';
import { PRODUCT_TYPES } from 'productTemplate/enums/productType';
import { setBuyBarSizesMap } from 'store/slice/buyBarSlice';
import { setIsFooterVisible } from 'store/slice/pageLayoutSlice';
import { setBuyBarVariants, setProductTemplateData, setUserWants } from 'store/slice/pdpSlice';
import { useIsNewMobilePdpAccessible } from 'shared/hooks/useIsNewMobilePdpAccessible';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';
import { useWindowSize } from 'shared/hooks/useWindowSize';
import { useFetchBuyBarVariantsQuery } from 'store/api/buyBarVariantsApi';
import { getSizesMap } from 'productTemplate/utils/getSizesMap';
import { useLocation } from 'shared/hooks/useLocation';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { FeatureFlags } from 'featureFlags/constants';

const ProductDetailPageLanding = dynamic(
  () => import('productTemplate/components/ProductDetailPageLanding'),
  { ssr: true },
);

const ProductDetailPageMobile = dynamic(
  () =>
    import('productTemplate/components/ProductDetailPageMobile').then(
      (mod) => mod.ProductDetailPageMobile,
    ),
  { ssr: true },
);

interface IProductDetailPage {
  factsWindowData: IConsumerSearchFactsWindowData;
  featuredInCollections: IFeaturedInCollection[];
  isCrawler: boolean;
  locale?: LOCALES;
  offers: IGmcOffers;
  productTemplate: IProductTemplate;
  recommendedProducts: Array<IRelatedProductData>;
}

const ProductDetailPage = ({
  factsWindowData,
  featuredInCollections,
  isCrawler = false,
  offers,
  productTemplate,
  locale,
  recommendedProducts,
}: IProductDetailPage) => {
  const router = useRouter();
  const { isTablet, isDesktop } = useWindowSize();
  const storeDispatch = useStoreDispatch();
  const { isAuthenticated } = useStoreSelector((state) => state.auth);
  const initialRender = useRef(false);
  const { selectedCountryCode } = useLocation();
  const { id, isAvailabilityRestricted, sizeOptions } = productTemplate;
  const isNewMobilePdpAccessible = useIsNewMobilePdpAccessible();
  const isUnavailabilityInRegionEnabled = useFeatureFlag(
    FeatureFlags.TEMP_WEB_ENABLE_UNAVAILABLE_IN_REGION,
  );
  const { data: buyBarVariantsData, isSuccess } = useFetchBuyBarVariantsQuery(
    {
      id,
      selectedCountryCode,
    },
    { skip: isUnavailabilityInRegionEnabled && isAvailabilityRestricted },
  );

  if (!initialRender.current) {
    storeDispatch(
      setProductTemplateData({
        factsWindowData,
        featuredInCollections,
        productTemplate,
      }),
    );
    initialRender.current = true;
  }

  useEffect(() => {
    storeDispatch(
      setProductTemplateData({
        factsWindowData,
        featuredInCollections,
        productTemplate,
      }),
    );
  }, [id]);

  useEffect(() => {
    if (isSuccess) {
      // New Mobile PDP
      storeDispatch(setBuyBarSizesMap(getSizesMap(buyBarVariantsData, sizeOptions)));
      // Destkop & Deprecated Mobile PDP
      storeDispatch(setBuyBarVariants(buyBarVariantsData));
    }
  }, [buyBarVariantsData, isSuccess, sizeOptions]);

  useEffect(() => {
    const fetchWants = async () => {
      const wants = await getWants(id);
      if (wants) {
        storeDispatch(setUserWants(wants));
      }
    };

    if (isAuthenticated) {
      void fetchWants();
    }
  }, [id, isAuthenticated]);

  useEffect(() => {
    if (!isCrawler) {
      // This prevents a huge layout shift on the mobile pdp. We add the Footer in the PDP
      storeDispatch(setIsFooterVisible(false));
    }

    return () => {
      storeDispatch(setIsFooterVisible(true));
    };
  }, [isCrawler]);

  useEffect(() => {
    const handleRouteChange = (url) => {
      // We do not want to clear the filters when we navigate between sneakers and apparel PDP
      // If route we're changing to is no longer a PDP, clear the search filters
      const urlSplit = url.split('/');
      const isSneakersOrApparel = new Set(['apparel', 'sneakers']);
      const isNotSneakersOrApparelCategory =
        !PRODUCT_TYPES.sneakers.has(urlSplit?.[2]) || !PRODUCT_TYPES.apparel.has(urlSplit?.[2]);
      const isUrlPdp =
        urlSplit.length === 3 &&
        isSneakersOrApparel.has(urlSplit?.[1]) &&
        isNotSneakersOrApparelCategory;

      if (!isUrlPdp) {
        storeDispatch(clearSearchFiltersForPdp());
      }
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router]);

  if (isTablet || isDesktop || !isNewMobilePdpAccessible) {
    return (
      <ProductDetailPageLanding
        isCrawler={isCrawler}
        offers={offers}
        productTemplate={productTemplate}
        locale={locale}
      />
    );
  }

  return (
    <ProductDetailPageMobile
      productTemplate={productTemplate}
      locale={locale}
      offers={offers}
      recommendedProducts={recommendedProducts}
    />
  );
};

export default ProductDetailPage;
